import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Injects JSON-LD structured data into the page <head>.
 * Usage: <JsonLd schema={schemaObject} />
 */
const JsonLd = ({ schema }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify(schema)}
    </script>
  </Helmet>
);

// ── Pre-built schema helpers ──────────────────────────────────────────────────

/** For the homepage */
export const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'StckFuture',
  url: 'https://www.stckfuture.com',
  description:
    'Free stock market education for beginners. Learn investing fundamentals, trading principles, risk management, and market analysis.',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.stckfuture.com/glossary',
    'query-input': 'required name=search_term_string',
  },
};

/** For educational article pages */
export const articleSchema = ({ title, description, url, dateModified }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  url,
  dateModified,
  author: {
    '@type': 'Organization',
    name: 'StckFuture',
    url: 'https://www.stckfuture.com',
  },
  publisher: {
    '@type': 'Organization',
    name: 'StckFuture',
    url: 'https://www.stckfuture.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.stckfuture.com/logo.png',
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': url,
  },
});

/** For the glossary page */
export const glossarySchema = {
  '@context': 'https://schema.org',
  '@type': 'DefinedTermSet',
  name: 'Stock Market Glossary',
  description:
    'Comprehensive definitions of 70+ stock market and investment terms for beginners.',
  url: 'https://www.stckfuture.com/glossary',
  publisher: {
    '@type': 'Organization',
    name: 'StckFuture',
    url: 'https://www.stckfuture.com',
  },
};

/** For the quiz page */
export const quizSchema = {
  '@context': 'https://schema.org',
  '@type': 'Quiz',
  name: 'Stock Market Knowledge Quiz',
  description:
    'Test your stock market knowledge with 10 random questions covering fundamentals, trading principles, risk management, and market analysis.',
  url: 'https://www.stckfuture.com/quiz',
  educationalLevel: 'Beginner',
  publisher: {
    '@type': 'Organization',
    name: 'StckFuture',
    url: 'https://www.stckfuture.com',
  },
};

/** For the trading platforms comparison page */
export const comparisonSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': 'https://www.stckfuture.com/trading-platforms',
  headline: 'Best Trading Platforms for Beginners 2026',
  description:
    'Compare the best stock trading platforms including Robinhood, Fidelity, Charles Schwab, TD Ameritrade, Webull, and Interactive Brokers.',
  url: 'https://www.stckfuture.com/trading-platforms',
  dateModified: '2026-06-13',
  author: {
    '@type': 'Organization',
    name: 'StckFuture',
  },
  publisher: {
    '@type': 'Organization',
    name: 'StckFuture',
    url: 'https://www.stckfuture.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.stckfuture.com/logo.png',
    },
  },
};

export default JsonLd;
